import React, { useState, useEffect, useCallback } from 'react';

interface SahkarPGQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentData: {
    amount_inr: number;
    devotee_name: string;
    devotee_phone: string;
    metal_type?: string;
    quantity_grams?: number;
    rate_per_gram?: number;
    block_id?: string;
  };
  qrCodeData: {
    order: {
      order_id: string;
      payment_link: string;
      amount: number;
      currency: string;
      gateway: string;
    };
    transaction: {
      id: string;
      status: string;
    };
  };
  onPaymentSuccess: (orderId: string) => void;
  onPaymentFailure: (error: string) => void;
}

const SahkarPGQRModal: React.FC<SahkarPGQRModalProps> = ({
  isOpen,
  onClose,
  paymentData,
  qrCodeData,
  onPaymentSuccess,
  onPaymentFailure
}) => {
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stopStatusCheck = useCallback(() => {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      setStatusCheckInterval(null);
    }
  }, [statusCheckInterval]);

  const handleCancel = useCallback(() => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      'Are you sure you want to cancel this payment? This will stop the payment process and you will need to start a new donation.'
    );
    
    if (confirmed) {
      // Stop status checking
      stopStatusCheck();
      // Close modal and reset state
      onClose();
    }
  }, [stopStatusCheck, onClose]);


  useEffect(() => {
    if (isOpen && qrCodeData) {
      setPaymentStatus('pending');
      setTimeLeft(900);
      setError(null);
      // Call the function directly without including it in dependencies
      // This is safe because the function is stable and we only want to start
      // the status check when the modal opens or qrCodeData changes
      const orderId = qrCodeData.transaction.id;
      console.log('🚀 Starting payment status check for order:', orderId);
      const interval = setInterval(async () => {
        try {
          // Check payment status using the transaction ID
          const API_BASE = process.env.REACT_APP_API_URL || '/api';
          console.log('🔍 Polling API endpoint:', `${API_BASE}/transactions/${orderId}`);
          console.log('🌐 Making API call to:', `${API_BASE}/transactions/${orderId}?t=${Date.now()}`);
          const response = await fetch(`${API_BASE}/transactions/${orderId}?t=${Date.now()}`, {
            cache: 'no-cache',
            headers: {
              'Cache-Control': 'no-cache'
            }
          });
          
          console.log('🔍 API response status:', response.status, 'ok:', response.ok);
          const data = await response.json();
          console.log('🔍 API response data:', data);
          
          if (data.success && data.data) {
            const transaction = data.data;
            console.log('🔍 Payment status check:', transaction);
            console.log('🔍 Transaction status:', transaction.status);
            console.log('🔍 Razorpay status:', transaction.razorpay_status);
            console.log('🔍 Augmont status:', transaction.augmont_status);
            console.log('🔍 Augmont transaction ID:', transaction.augmont_transaction_id);
            
            // Check for payment success indicators
            const isPaymentSuccess = transaction.status === 'completed' || 
                                   transaction.status === 'processing' ||
                                   (transaction.razorpay_status === 'captured') ||
                                   (transaction.augmont_status === 'completed') ||
                                   (transaction.augmont_transaction_id && transaction.augmont_transaction_id !== '');
            
            if (isPaymentSuccess) {
              console.log('✅ Payment completed successfully! Status:', transaction.status, 'Razorpay:', transaction.razorpay_status, 'Augmont:', transaction.augmont_status);
              setPaymentStatus('success');
              clearInterval(interval);
              setStatusCheckInterval(null);
              onPaymentSuccess(orderId);
            } else if (transaction.status === 'failed') {
              console.log('❌ Payment failed');
              setPaymentStatus('failed');
              setError('Payment failed. Please try again.');
              clearInterval(interval);
              setStatusCheckInterval(null);
              onPaymentFailure('Payment failed');
            } else if (transaction.status === 'pending') {
              console.log('⏳ Payment pending...');
              // Continue checking
            } else {
              console.log('📊 Payment status:', transaction.status);
              // Continue checking
            }
          } else {
            console.log('⚠️ Status check response:', data);
          }
        } catch (err) {
          console.error('Error checking payment status:', err);
        }
      }, 3000); // Check every 3 seconds

      setStatusCheckInterval(interval);

      // Clear interval after 15 minutes
      setTimeout(() => {
        setPaymentStatus(prevStatus => {
          if (prevStatus === 'pending') {
            console.log('⏰ Payment timeout after 15 minutes');
            setError('Payment timeout. Please try again.');
            clearInterval(interval);
            setStatusCheckInterval(null);
            onPaymentFailure('Payment timeout');
            return 'failed';
          }
          return prevStatus;
        });
      }, 900000);
    }
  }, [isOpen, qrCodeData, onPaymentSuccess, onPaymentFailure]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setPaymentStatus('failed');
      setError('Payment timeout. Please try again.');
      stopStatusCheck();
    }
    return () => clearTimeout(timer);
  }, [timeLeft, isOpen, stopStatusCheck]);

  useEffect(() => {
    return () => {
      stopStatusCheck();
    };
  }, [stopStatusCheck]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Generate QR code image URL from payment link
  const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeData.order.payment_link)}`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">Sahkar PG Payment</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="ml-3 text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-800 mb-2">Payment Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-medium">₹{paymentData.amount_inr.toLocaleString()}</span>
              </div>
              {paymentData.quantity_grams && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Gold Quantity:</span>
                  <span className="font-medium">{paymentData.quantity_grams}g</span>
                </div>
              )}
              {paymentData.rate_per_gram && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Rate:</span>
                  <span className="font-medium">₹{paymentData.rate_per_gram}/g</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Devotee:</span>
                <span className="font-medium">{paymentData.devotee_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Order ID:</span>
                <span className="font-mono text-xs">{qrCodeData.order.order_id}</span>
              </div>
            </div>
          </div>

          {/* Timer */}
          <div className="text-center mb-6">
            <div className={`text-2xl font-bold ${timeLeft <= 60 ? 'text-red-600' : timeLeft <= 300 ? 'text-orange-600' : 'text-blue-600'}`}>
              {formatTime(timeLeft)}
            </div>
            <p className="text-sm text-gray-600">Time remaining to complete payment</p>
            {timeLeft <= 60 && (
              <p className="text-sm text-red-600 font-medium mt-2">
                ⚠️ Payment will expire soon! Please complete payment quickly.
              </p>
            )}
          </div>

          {/* QR Code */}
          <div className="text-center mb-6">
            <h3 className="font-semibold text-gray-800 mb-4">Scan QR Code to Pay</h3>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4 inline-block">
              <img
                src={qrImageUrl}
                alt="Sahkar PG QR Code"
                className="w-48 h-48 mx-auto"
              />
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Sahkar Payment Gateway
            </p>
          </div>

          {/* Payment Status & Timer */}
          {paymentStatus === 'pending' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-blue-800 font-medium">Waiting for payment...</span>
                </div>
                <div className="text-blue-600 font-mono text-sm">
                  ⏰ {formatTime(timeLeft)}
                </div>
              </div>
              <p className="text-blue-600 text-xs mt-2">
                Please complete payment within {formatTime(timeLeft)}. Status checking every 3 seconds.
              </p>
            </div>
          )}

          {/* Payment Instructions */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">How to Pay</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-lg p-3">
                <h4 className="font-medium text-blue-800 text-sm mb-1">Option 1: Scan QR Code</h4>
                <p className="text-xs text-blue-600">Open any UPI app (Google Pay, PhonePe, Paytm, BHIM) and scan the QR code above</p>
              </div>
            </div>
          </div>

          {/* Status */}
          {paymentStatus === 'success' && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6 mb-6">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-2">Payment Successful!</h3>
                <p className="text-green-700 mb-4">Thank you for your donation. Your gold order has been created successfully.</p>
                <div className="bg-white rounded-lg p-3 border border-green-200">
                  <div className="text-sm text-green-600">
                    <div className="flex justify-between mb-1">
                      <span>Amount:</span>
                      <span className="font-medium">₹{paymentData.amount_inr.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span>Order ID:</span>
                      <span className="font-mono text-xs">{qrCodeData.order.order_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <span className="font-medium text-green-600">✅ Confirmed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6 mb-6">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">Payment Failed</h3>
                <p className="text-red-700 mb-4">The payment could not be completed. Please try again.</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-between space-x-3 mt-6">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-red-600 border border-red-300 rounded hover:bg-red-50"
            >
              Cancel & Start New Donation
            </button>
            <div className="flex space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SahkarPGQRModal;
